import api from './api';

export async function fetchAllProducts() {
  const res = await api.get('/products');
  return res.data;
}

// Fetch products directly from database (bypasses cache) for admin trade
// Returns ALL products regardless of status
export async function fetchProductsForAdminTrade() {
  const res = await api.get('/products/for-admin-trade');
  return res.data;
}

export async function updateProduct(productId, data) {
  const res = await api.put(`/products/${productId}`, data);
  return res.data;
}

export async function toggleProductActive(productId, isActive) {
  const res = await api.put(`/products/${productId}`, { isActive });
  return res.data;
}

export async function reorderProducts(orders) {
  const res = await api.post('/products/reorder', { orders });
  return res.data;
}

function isTraderProductGroup(entry) {
  return Boolean(
    entry &&
    typeof entry === "object" &&
    Array.isArray(entry.items) &&
    (entry.key != null || entry.title != null || entry.sortOrder != null)
  );
}

function attachTraderProductGroup(product, group, index) {
  return {
    ...product,
    groupKey: group?.key || product?.groupKey || `group-${index}`,
    groupTitle: group?.title ?? product?.groupTitle ?? "",
    groupCategory: group?.category ?? product?.groupCategory ?? null,
    groupGoldQuiddity: group?.goldQuiddity ?? product?.groupGoldQuiddity ?? product?.goldQuiddity ?? null,
    groupSortOrder: group?.sortOrder ?? product?.groupSortOrder ?? index,
  };
}

function flattenTraderProductGroups(groups) {
  return [...groups]
    .map((group, index) => ({ group, index }))
    .sort((a, b) => {
      const orderDiff = (a.group?.sortOrder ?? 0) - (b.group?.sortOrder ?? 0);
      return orderDiff !== 0 ? orderDiff : a.index - b.index;
    })
    .flatMap(({ group, index }) => {
      const items = Array.isArray(group?.items) ? group.items : [];
      return items
        .filter((product) => product && typeof product === "object")
        .map((product) => attachTraderProductGroup(product, group, index));
    });
}

/**
 * Normalize traderproductlist payloads.
 * Current shape: { groups: [{ key, title, category, goldQuiddity, sortOrder, items }] }
 * Legacy shape: product[]
 * @returns {Array} Flat products with group metadata, groups ordered by sortOrder.
 */
export function normalizeTraderProductList(payload) {
  if (!payload) return [];

  if (Array.isArray(payload)) {
    if (payload.some(isTraderProductGroup)) {
      return flattenTraderProductGroups(payload.filter(isTraderProductGroup));
    }

    return payload
      .filter((product) => product && typeof product === "object")
      .map((product, index) => attachTraderProductGroup(product, {
        key: product.groupKey || "cat-gold",
        title: product.groupTitle || "طلا",
        category: product.groupCategory ?? null,
        goldQuiddity: product.goldQuiddity ?? null,
        sortOrder: product.groupSortOrder ?? 0,
      }, index));
  }

  if (Array.isArray(payload.groups)) {
    return flattenTraderProductGroups(payload.groups);
  }

  return [];
}

function readPriceValue(price) {
  if (typeof price === "object" && price !== null) {
    return price.value ?? 0;
  }
  return price || 0;
}

// Melted gold (آب شده) groups have goldQuiddity === 1 and carry per-gram prices.
function isMeltedGoldProduct(product) {
  return Number(product?.groupGoldQuiddity ?? product?.goldQuiddity) === 1;
}

/** Map one trader product into the price-row shape used by the store and order modal. */
export function mapTraderProductToPriceItem(product) {
  const productEnabled = product.isEnable !== false;
  const productVariableType = product.productVariableType;
  const hasGramPrices = isMeltedGoldProduct(product);

  return {
    id: product.id,
    name: product.name,
    buy: String(readPriceValue(product.buyPrice)),
    sell: String(readPriceValue(product.sellPrice)),
    buyEnabled: productEnabled ? product.buyIsEnable !== false : false,
    sellEnabled: productEnabled ? product.sellIsEnable !== false : false,
    isEnable: product.isEnable,
    buyPrice: product.buyPrice,
    sellPrice: product.sellPrice,
    gramBuy: hasGramPrices ? readPriceValue(product.gramBuyPrice) : 0,
    gramSell: hasGramPrices ? readPriceValue(product.gramSellPrice) : 0,
    productVariableType,
    tradingProductType: product.tradingProductType ?? null,
    unit: product.unit ?? null,
    isSys: product.isSys ?? null,
    karat: product.karat ?? null,
    unitType: productVariableType === 2 ? "COUNT" : "GRAM18",
    startTime: product.startTime || null,
    endTime: product.endTime || null,
    lastPriceUpdateTime: product.lastUpdate || product.lastPriceUpdateTime || new Date().toISOString(),
    rateId: product.rateId ?? null,
    groupKey: product.groupKey ?? null,
  };
}

/**
 * Group a flat trader product list into display categories.
 * Group order follows sortOrder, then the order products were received.
 */
export function groupTraderProductsIntoCategories(products) {
  if (!Array.isArray(products) || products.length === 0) return [];

  const categories = [];
  const byKey = new Map();

  products.forEach((product, index) => {
    const key = product?.groupKey || "ungrouped";
    let category = byKey.get(key);
    if (!category) {
      category = {
        id: key,
        title: product?.groupTitle || "سایر",
        category: product?.groupCategory ?? null,
        goldQuiddity: product?.groupGoldQuiddity ?? product?.goldQuiddity ?? null,
        sortOrder: product?.groupSortOrder ?? index,
        items: [],
      };
      byKey.set(key, category);
      categories.push(category);
    }
    category.items.push(mapTraderProductToPriceItem(product));
  });

  return categories.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

/**
 * Fetch trader product list with prices
 * Display quotes in online transactions
 * @returns {Promise<Array>} Flat trader products, each tagged with its category group
 */
export async function fetchTraderProductList() {
  const { data } = await api.get('/trading/product/traderproductlist', {
    // Some environments aggressively cache GET responses for this endpoint.
    headers: {
      "Cache-Control": "no-cache, no-store, must-revalidate",
      Pragma: "no-cache",
      Expires: "0",
    },
  });

  if (!data.isSuccessful) {
    const errorMessage = data.getMessageText?.join(", ") || data.message?.[0]?.messageText || "دریافت لیست قیمت‌ها ناموفق بود";
    throw new Error(errorMessage);
  }

  return normalizeTraderProductList(data.data);
}

/**
 * Update product quotes in online transactions (Admin)
 * Edit quotes in online transactions
 * @param {Object} productData - Product data to update
 * @returns {Promise<Object>} Updated product data
 */
export async function updateAdminProduct(productData) {
  const { data } = await api.post('/trading/product/adminproductupdate', productData);

  if (!data.isSuccessful) {
    const errorMessage = data.getMessageText?.join(", ") || data.message?.[0]?.messageText || "ویرایش مظنه ناموفق بود";
    throw new Error(errorMessage);
  }

  return data.data;
}

/**
 * Fetch admin product list
 * List quotes in online transactions
 * @returns {Promise<Array>} Array of admin products
 */
export async function fetchAdminProductList() {
  const { data } = await api.get('/trading/product/adminproductlist');

  if (!data.isSuccessful) {
    const errorMessage = data.getMessageText?.join(", ") || data.message?.[0]?.messageText || "دریافت لیست مظنه‌ها ناموفق بود";
    throw new Error(errorMessage);
  }

  return data.data || [];
}

/**
 * Create product for coin
 * @param {Object} productData - Product data to create
 * @returns {Promise<Object>} Created product data
 */
export async function createProductForCoin(productData) {
  const { data } = await api.post('/trading/product/createforcoin', productData);

  if (!data.isSuccessful) {
    const errorMessage = data.getMessageText?.join(", ") || data.message?.[0]?.messageText || "ایجاد مظنه ناموفق بود";
    throw new Error(errorMessage);
  }

  return data.data;
}
